import Stack from '@mui/material/Stack';
import useMediaQuery from '@mui/material/useMediaQuery';
import { type Theme } from '@mui/material/styles';
import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { IdCard } from 'lucide-react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { BlurModal } from '@/pages/Settings/Models/Edit/style';
import { Button } from '../Button';
import {
  EditCard,
  EditStack,
  EmailField,
  InfoStack,
  NameField,
  UserIdChip,
  UserRoleChip,
} from './styles';
import { Formik } from 'formik';
import { userEditSchema } from '@/validations/userEditSchema';
import { enqueueSnackbar } from 'notistack';
import api from '@/services/api';
import axios from 'axios';
import ChangePassword from '../ChangePassword';
import ProfilePicture from './ProfilePicture';
import { useProfileImage } from '@/hooks/useProfileImage';
import { useUser } from '@/context/UserContext';

interface userProps {
  name?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
}

function EditUser({
  user,
  open,
  setOpen,
}: {
  user: User;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const { updateUser } = useUser();
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);

  const [changePassword, setChangePassword] = useState(false);

  const {
    imagePreview,
    handleFileSelect,
    handleRemoveImage,
    handleConfirmUpload,
    confirmDialogOpen,
    setConfirmDialogOpen,
  } = useProfileImage();

  const [value, setValue] = useState(false);

  useEffect(() => {
    if (value) {
      handleConfirmUpload();
    }
    setValue(false);
  }, [value, confirmDialogOpen]);

  const [initialValues, setInitialValues] = useState({
    name: user ? user.name : '',
    email: user ? user.email : '',
    password: '**********',
    confirmPassword: '**********',
  });

  const confirmPassword = () => {
    setChangePassword(false);
    setOpen(false);
  };

  // check changed fields and submit
  const handleSubmit = async () => {
    const checkValues = initialValues;
    const updatedFields: userProps = {};
    console.log('Initial Values:', initialValues);

    if (name !== checkValues.name) updatedFields.name = name;
    if (email !== checkValues.email) updatedFields.email = email;

    console.log('Campos atualizados:', updatedFields);

    if (Object.keys(updatedFields).length === 0) {
      enqueueSnackbar(t('config.personalData.noUpdate'), {
        variant: 'warning',
      });
      console.log('No changes detected.');
      return;
    } else {
      try {
        const response = await api.put(`users/${user.id}`, {
          name: name,
          email: email,
          role: user.role,
        });

        console.log(response);

        if (response.status === 200) {
          updateUser(response.data);
          setInitialValues({ ...initialValues, ...updatedFields });
          setOpen(false);
        }
      } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
          if (error.response) {
            enqueueSnackbar(error.response.data?.detail, {
              variant: 'error',
            });
          }
        }
      }
    }
  };

  return (
    <BlurModal open={open} onClose={() => setOpen(false)}>
      <EditCard $isSm={isSm}>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={userEditSchema}
        >
          {({ values }) => (
            <EditStack $isSm={isSm}>
              <ProfilePicture
                key={imagePreview || 'no-image'}
                user={user}
                imagePreview={imagePreview}
                onFileSelect={handleFileSelect}
                onRemoveClick={handleRemoveImage}
                setValue={setValue}
                setOpenConfirmDialog={setConfirmDialogOpen}
                confirmDialogOpen={confirmDialogOpen}
              />
              <InfoStack>
                <Stack direction="row" spacing={1}>
                  <UserIdChip
                    icon={<IdCard color={theme.palette.fixed.black} />}
                    color="primary"
                    label={user.id}
                  />
                  <UserRoleChip label={t(`config.users.${user.role}`)} />
                </Stack>
                <Stack direction="column" spacing={1}>
                  <NameField
                    id="name"
                    variant="standard"
                    value={name}
                    onChange={(e: { target: { value: string } }) =>
                      setName(e.target.value)
                    }
                    input={values.name}
                  />
                  <EmailField
                    input={values.email}
                    variant="standard"
                    value={email}
                    onChange={(e: { target: { value: string } }) =>
                      setEmail(e.target.value)
                    }
                  />
                  <Button
                    variant="text"
                    onClick={() => setChangePassword(true)}
                    sx={{ width: 'max-content' }}
                  >
                    {t('config.users.editUser.changePassword')}
                  </Button>
                </Stack>
                <div style={{ flex: 1 }} />
                <Stack direction="row" spacing={2}>
                  <Button variant="contained" onClick={() => handleSubmit()}>
                    {t('common.save')}
                  </Button>
                  <Button variant="outlined" onClick={() => setOpen(false)}>
                    {t('common.cancel')}
                  </Button>
                </Stack>
              </InfoStack>
            </EditStack>
          )}
        </Formik>
        {changePassword && (
          <ChangePassword
            open={changePassword}
            handleClose={() => confirmPassword()}
          />
        )}
      </EditCard>
    </BlurModal>
  );
}

export default EditUser;
